/**
 * IngredientFormList Component
 *
 * Manages a list of IngredientRow components for the recipe form.
 * Supports adding and removing ingredients.
 */

import type { Ingredient } from '@/types/food';
import { IngredientRow, type RecipeIngredientFormData } from './IngredientRow';

interface IngredientFormListProps {
  ingredients: RecipeIngredientFormData[];
  availableIngredients: Ingredient[];
  onChange: (ingredients: RecipeIngredientFormData[]) => void;
}

/**
 * Plus icon for add button
 */
function PlusIcon() {
  return (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  );
}

export function IngredientFormList({
  ingredients,
  availableIngredients,
  onChange,
}: IngredientFormListProps) {
  // Filter out destroyed ingredients for display
  const visibleIngredients = ingredients.filter((ing) => !ing._destroy);

  const handleIngredientChange = (
    index: number,
    updated: RecipeIngredientFormData
  ) => {
    const newIngredients = [...ingredients];
    // Find the actual index in the full array (including destroyed)
    let visibleIndex = 0;
    for (let i = 0; i < newIngredients.length; i++) {
      if (!newIngredients[i]._destroy) {
        if (visibleIndex === index) {
          newIngredients[i] = updated;
          break;
        }
        visibleIndex++;
      }
    }
    onChange(newIngredients);
  };

  const handleRemove = (index: number) => {
    const newIngredients = [...ingredients];
    // Find the actual index in the full array (including destroyed)
    let visibleIndex = 0;
    for (let i = 0; i < newIngredients.length; i++) {
      if (!newIngredients[i]._destroy) {
        if (visibleIndex === index) {
          // If it has an id, mark as destroyed; otherwise remove
          if (newIngredients[i].id) {
            newIngredients[i] = { ...newIngredients[i], _destroy: true };
          } else {
            newIngredients.splice(i, 1);
          }
          break;
        }
        visibleIndex++;
      }
    }
    // Update positions
    let position = 1;
    newIngredients.forEach((ing) => {
      if (!ing._destroy) {
        ing.position = position++;
      }
    });
    onChange(newIngredients);
  };

  const handleAdd = () => {
    const newIngredient: RecipeIngredientFormData = {
      ingredient_id: 0,
      quantity: null,
      unit: '',
      notes: '',
      optional: false,
      position: visibleIngredients.length + 1,
    };
    onChange([...ingredients, newIngredient]);
  };

  return (
    <div className="flex flex-col gap-3">
      {visibleIngredients.map((ingredient, index) => (
        <IngredientRow
          key={ingredient.id || `new-${index}`}
          ingredient={ingredient}
          availableIngredients={availableIngredients}
          onChange={(updated) => handleIngredientChange(index, updated)}
          onRemove={() => handleRemove(index)}
          index={index}
        />
      ))}

      {/* Add ingredient button */}
      <button
        type="button"
        onClick={handleAdd}
        className="flex items-center gap-2 p-4 border-2 border-dashed border-gray-200 rounded-lg text-gray-500 hover:border-blue-600 hover:text-blue-600 hover:bg-blue-50 transition-colors"
      >
        <PlusIcon />
        <span className="text-sm font-medium">Add Ingredient</span>
      </button>
    </div>
  );
}
